import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { api, errMsg, num, rupee, pnlClass, istDateTime } from "@/lib/api";
import { CoveredCallIdeas } from "@/components/CoveredCallIdeas";

const Bar = ({ label, cur, target, id, fmt = (v) => v, sub }) => {
  const pct = Math.min(100, ((cur || 0) / target) * 100);
  return (
    <div data-testid={id}>
      <div className="flex items-baseline justify-between text-xs">
        <span className="text-slate-300">{label}</span>
        <span className="num text-slate-400"><b className="text-slate-100">{cur === null || cur === undefined ? "—" : fmt(cur)}</b> / {fmt(target)}</span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded bg-slate-800">
        <div className={`h-full transition-[width] duration-700 ${pct >= 100 ? "bg-emerald-500" : "bg-indigo-500"}`} style={{ width: `${pct}%` }} />
      </div>
      {sub && <div className="mt-1 text-[11px] text-slate-500">{sub}</div>}
    </div>
  );
};

export default function AccountTab({ status }) {
  const [v, setV] = useState(null);
  const [acc, setAcc] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/validation").then((r) => setV(r.data)).catch((e) => setErr(errMsg(e)));
    if (status?.status === "connected") api.get("/account").then((r) => setAcc(r.data)).catch((e) => setErr(errMsg(e)));
  }, [status?.status]);

  const pfv = (x) => (x === null || x === undefined ? null : Math.min(x, 99));
  const passed = v && v.oos_trades >= v.oos_target && v.paper_trades >= v.paper_target_min && pfv(v.oos_pf) >= v.pf_target && pfv(v.paper_pf) >= v.pf_target;
  const f = acc?.funds || {};
  return (
    <div className="grid grid-cols-12 gap-3 sm:gap-4 fade-in">
      <div className="panel col-span-12 p-4 lg:col-span-6" data-testid="panel-validation-tracker">
        <div className="flex items-center justify-between">
          <span className="eyebrow">Validation before live execution</span>
          <span data-testid="validation-verdict" className={`rounded border px-2 py-0.5 text-[10px] font-semibold tracking-wider ${passed ? "border-emerald-500/40 text-emerald-400" : "border-slate-600 text-slate-400"}`}>
            {passed ? "TARGETS MET" : "IN PROGRESS"}
          </span>
        </div>
        {v && (
          <div className="mt-4 space-y-5">
            <Bar id="tracker-oos-trades-progress" label="Out-of-sample backtest trades" cur={v.oos_trades} target={v.oos_target} sub={v.backtest_range ? `from backtest ${v.backtest_range} (latest with 200+ OOS trades, run ${istDateTime(v.backtest_at)})` : "no backtest yet"} />
            <Bar id="tracker-paper-trades-progress" label={`Closed paper trades (goal ${v.paper_target_min}–${v.paper_target_max})`} cur={v.paper_trades} target={v.paper_target_min} />
            <Bar id="tracker-profit-factor-progress" label="Out-of-sample Profit Factor" cur={pfv(v.oos_pf)} target={v.pf_target} fmt={(x) => num(x)} />
            <Bar id="tracker-paper-pf-progress" label="Paper Profit Factor" cur={pfv(v.paper_pf)} target={v.pf_target} fmt={(x) => num(x)} />
          </div>
        )}
        <div className="mt-6 flex items-start gap-3 rounded border border-slate-800 bg-slate-900/60 p-3 text-xs text-slate-400" data-testid="live-lock-explainer">
          <Lock size={16} className="mt-0.5 shrink-0 text-slate-500" />
          Live auto-trading stays blocked until all of these targets are met, the server flag LIVE_TRADING_ENABLED is on, and you arm it each day on the Live Trading tab.
        </div>
      </div>

      <div className="col-span-12 flex flex-col gap-3 lg:col-span-6">
        <div className="panel p-4" data-testid="panel-dhan-funds">
          <div className="eyebrow mb-3">Dhan funds · read-only</div>
          {status?.status !== "connected" ? <p className="text-sm text-slate-500">Connect Dhan to view funds.</p> : err ? <p className="text-xs text-red-400">{err}</p> : (
            <div className="grid grid-cols-2 gap-3 text-xs">
              {[["Available", f.availabelBalance ?? f.availableBalance], ["SOD limit", f.sodLimit], ["Utilized", f.utilizedAmount], ["Withdrawable", f.withdrawableBalance]].map(([k, val]) => (
                <div key={k}><div className="eyebrow !text-[10px]">{k}</div><div className="num text-lg font-semibold" data-testid={`fund-${k.toLowerCase().replace(" ", "-")}`}>{rupee(val)}</div></div>
              ))}
            </div>
          )}
        </div>
        <div className="panel" data-testid="panel-dhan-positions">
          <div className="border-b border-slate-800 px-4 py-2.5"><span className="eyebrow">Dhan positions · read-only</span></div>
          <div className="overflow-x-auto">
            <table className="dense num w-full">
              <thead><tr><th>Symbol</th><th>Type</th><th>Net qty</th><th>Buy avg</th><th>Sell avg</th><th>Realized</th><th>Unrealized</th></tr></thead>
              <tbody>
                {(!acc?.positions || acc.positions.length === 0) && <tr><td colSpan={7} className="!py-6 !text-center text-slate-500">No positions.</td></tr>}
                {acc?.positions?.map((p, i) => (
                  <tr key={i}><td>{p.tradingSymbol}</td><td>{p.positionType}</td><td>{p.netQty}</td><td>{num(p.buyAvg)}</td><td>{num(p.sellAvg)}</td>
                    <td className={pnlClass(p.realizedProfit)}>{rupee(p.realizedProfit)}</td><td className={pnlClass(p.unrealizedProfit)}>{rupee(p.unrealizedProfit)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <CoveredCallIdeas connected={status?.status === "connected"} />
    </div>
  );
}
